import { useContext } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { DataContext } from '@aytm/app/stats/page/sidebar/reports/ReportsPanel'

import { isSkipperGuidedDraftReport } from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReport'

import {
  STATIC_REPORTS_IDS,
  VIRTUAL_REPORTS_IDS,
  addListItem,
  revertListItem,
  updateListItem
} from '@aytm/app/stats/page/sidebar/reports/actions'

// components
import { Button } from 'primereact/button'
import { SplitButton } from 'primereact/splitbutton'

// styles
import style from '@aytm/app/stats/page/sidebar/reports/styles/ReportPopup.module.scss'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

const ReportControls = ({ model, isCompact }) => {
  const { state, listModel, isSurveyGuest, isSurveyViewer, disabled, dispatch, onReportsChange, onReportsError } =
    useContext(DataContext)

  const modelState = state[model.id] || {}

  const schema = model.current.schema
  const isChanged = model.changed
  const isProcessing = Object.hasOwn(modelState, 'process')
  const isEmpty =
    (null !== schema && !schema.length && !['xpert', 'xpert_custom'].includes(model.current.type)) ||
    isSkipperGuidedDraftReport(model.current)
  // whether this reader's draft still carries the report's last-saved version: a stale draft is steered to
  // Update or Save as, so it can never overwrite a version saved since
  const isEditable =
    model.editable &&
    !['original', 'xpert', 'widget', ...STATIC_REPORTS_IDS, ...VIRTUAL_REPORTS_IDS].includes(model.current.type)
  const isPanelDisabled = disabled
  const atReportsCap = 250 <= listModel.length
  const isDisabled = isPanelDisabled || atReportsCap
  const isLocked = ['widget'].includes(model.current.type) || isProcessing || isSurveyGuest || isSurveyViewer
  const isAdding = isProcessing && 'add' == modelState.process
  const isSaving = isProcessing && 'save' == modelState.process

  const getModelProps = () => {
    return {
      url: PageGlobals.STAT_REPORT_URL,
      listModel,
      model,
      onReportsChange,
      onReportsError
    }
  }

  const onReportAdd = () => {
    addListItem(dispatch, { ...getModelProps() })
  }

  const onReportRevert = () => {
    revertListItem(dispatch, { ...getModelProps() })
  }

  const onReportUpdate = () => {
    updateListItem(dispatch, { ...getModelProps() })
  }

  const options = []
  if (isCompact && !isSurveyGuest && !isSurveyViewer) {
    isChanged &&
      options.push({
        label: isEditable
          ? 'Revert'
          : ['original', 'xpert', 'widget', ...STATIC_REPORTS_IDS, ...VIRTUAL_REPORTS_IDS].includes(model.current.type)
            ? 'Reset'
            : 'Update',
        command: onReportRevert
      })
    isEditable &&
      !isEmpty &&
      !isDisabled && // the report ceiling gates this entry as it gates the primary action
      options.push({
        label: 'Save as...',
        command: onReportAdd
      })
  }

  // the action the primary half will run, null when it has none to offer. The ceiling closes
  // adds only, so an update on an existing report stays available at it.
  const primaryAction = isCompact
    ? isEmpty || isPanelDisabled || isLocked
      ? null
      : isEditable
        ? isChanged
          ? onReportUpdate
          : null
        : atReportsCap
          ? null
          : onReportAdd
    : null

  return isCompact ? (
    0 < options.length ? (
      <SplitButton
        className={classNames('p-button-sm p-button-rounded p-button-blue', {
          // presentation only -- a disabled prop would reach the dropdown, and at the
          // ceiling Revert is the one action left in it
          'p-button-disabled': !primaryAction
        })}
        model={options}
        label={isAdding || isSaving ? 'Saving...' : isEditable ? (isChanged ? 'Save' : 'Saved') : 'Save as'}
        icon={isAdding || isSaving ? null : isChanged ? 'pi pi-warning' : null}
        dropdownIcon={isAdding || isSaving ? 'pi pi-spin pi-spinner' : <i className="icon-aytmfd-arrow-v2" />}
        appendTo="self"
        onClick={primaryAction}
      />
    ) : isSurveyGuest || isSurveyViewer ? (
      <Button
        className="p-button-sm p-button-rounded p-button-blue"
        label={
          isChanged
            ? isEditable
              ? 'Revert'
              : ['original', 'xpert', 'widget', ...STATIC_REPORTS_IDS, ...VIRTUAL_REPORTS_IDS].includes(
                    model.current.type
                  )
                ? 'Reset'
                : 'Update'
            : 'Saved'
        }
        loading={isProcessing && 'revert' == modelState.process}
        disabled={disabled || isProcessing || !isChanged}
        onClick={disabled || isProcessing || !isChanged ? null : onReportRevert}
      />
    ) : (
      <Button
        className="p-button-sm p-button-rounded p-button-blue"
        label={isAdding ? 'Saving...' : 'Save as'}
        iconPos="right"
        loading={isAdding}
        disabled={isEmpty || isDisabled || isLocked}
        onClick={isEmpty || isDisabled || isLocked ? null : onReportAdd}
      />
    )
  ) : isChanged ? (
    <div className={classNames(style['options-row'], style['options-row-buttons'])}>
      <Button
        className="p-button-sm p-button-rounded p-button-blue p-button-secondary"
        label={
          isEditable
            ? 'Revert'
            : ['original', 'xpert', 'widget', ...STATIC_REPORTS_IDS, ...VIRTUAL_REPORTS_IDS].includes(
                  model.current.type
                )
              ? 'Reset'
              : 'Update'
        }
        loading={isProcessing && 'revert' == modelState.process}
        disabled={disabled || isProcessing}
        onClick={disabled || isProcessing ? null : onReportRevert}
      />
      {isEditable ? (
        <Button
          className="p-button-sm p-button-rounded p-button-blue"
          label={isAdding || isSaving ? 'Saving' : 'Save for everyone'}
          icon={isAdding || isSaving ? null : isChanged ? 'pi pi-warning' : null}
          loading={isAdding || isSaving}
          disabled={disabled || isEmpty || isLocked}
          onClick={disabled || isEmpty || isLocked ? null : onReportUpdate}
        />
      ) : (
        <Button
          className="p-button-sm p-button-rounded p-button-blue"
          label={isAdding || isSaving ? 'Saving' : 'Save as a new report'}
          icon={isAdding || isSaving ? null : isChanged ? 'pi pi-warning' : null}
          loading={isAdding || isSaving}
          disabled={isEmpty || isDisabled || isLocked}
          onClick={isEmpty || isDisabled || isLocked ? null : onReportAdd}
        />
      )}
    </div>
  ) : null
}

ReportControls.propTypes = {
  model: PropTypes.object.isRequired,
  isCompact: PropTypes.bool
}

ReportControls.displayName = 'ReportControls'

export default ReportControls
